import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { MatTableDataSource } from '@angular/material/table';
import { deepCopy, isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { BehaviorSubject, distinctUntilChanged, filter, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostTypeCostCenterDistribution } from '../../../models/cost-type-cost-center-distribution.model';
import { ICostType } from '../../../models/cost-type.model';
import { IJoinCostTypeCostCenterState } from '../../../services/facades/master-data/join-cost-type-cost-center-state.model';
import { JoinCostTypeCostCenterFacade } from '../../../services/facades/master-data/join-cost-type-cost-center.facade';
import { JoinCostTypeCostCenterDialogComponent } from './join-cost-type-cost-center-dialog/join-cost-type-cost-center-dialog.component';

@Component({
    templateUrl: './join-cost-type-cost-center.page.html',
    styleUrls: [ './join-cost-type-cost-center.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JoinCostTypeCostCenterPage implements OnInit, OnDestroy {

    public dataSource: MatTableDataSource<ICostTypeCostCenterDistribution> = new MatTableDataSource<ICostTypeCostCenterDistribution>([]);

    public joinCostTypeCostCenterState$: Observable<IJoinCostTypeCostCenterState>;
    public distributionReferenceFormControl: FormControl = new FormControl({ value: null });
    public searchFormControl: FormControl<string> = new FormControl();
    public filteredCostTypes$: Observable<ICostType[]> = new BehaviorSubject<ICostType[]>([]);
    private destroy$: Subject<void> = new Subject();

    constructor(private myJoinCostTypeCostCenterFacade: JoinCostTypeCostCenterFacade,
                private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.joinCostTypeCostCenterState$ = this.myJoinCostTypeCostCenterFacade.subState();
        this.joinCostTypeCostCenterState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IJoinCostTypeCostCenterState) => s.distributions))
            .subscribe((data: ICostTypeCostCenterDistribution[]) => {
                this.dataSource.data = data;
            });
        this.joinCostTypeCostCenterState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IJoinCostTypeCostCenterState) => s.selectedCostType),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((selectedCostType: ICostType) => {
                if (selectedCostType === null) {
                    this.distributionReferenceFormControl.disable();
                } else {
                    this.distributionReferenceFormControl.setValue(selectedCostType.distributionReferenceId, { emitEvent: false });
                    this.distributionReferenceFormControl.enable();
                }
            });
        this.joinCostTypeCostCenterState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IJoinCostTypeCostCenterState) => s.costTypes),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((costTypes: ICostType[]) => {
                this.filteredCostTypes$ = this.searchFormControl.valueChanges.pipe(
                    startWith(null),
                    switchMap((res: string) => {
                        if (!res) {return of(costTypes);}
                        res = res.toLowerCase();
                        return of(costTypes.filter((c: ICostType) => c.name.toLowerCase().indexOf(res) >= 0));
                    }),
                );
            });

        void this.myJoinCostTypeCostCenterFacade.fetchData();
        this.listenToDistributionReferenceSelectionChange();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myJoinCostTypeCostCenterFacade.clearState();
    }

    public onSelectKostenart(event: MatSelectionListChange): void {
        const selectedCostType: ICostType = deepCopy(event.source._value[ 0 ]) as unknown as ICostType;
        this.searchFormControl.setValue(selectedCostType.name, { emitEvent: false });
        void this.myJoinCostTypeCostCenterFacade.setSelectedCostType(selectedCostType);
    }

    public onOpenDialog(distribution?: ICostTypeCostCenterDistribution): void {
        this.myMatDialog.open(JoinCostTypeCostCenterDialogComponent, {
            data: isNullOrUndefined(distribution) ? { variable: false } : distribution,
        });
    }

    public onDeleteDistribution(distribution: ICostTypeCostCenterDistribution): void {
        const state: IJoinCostTypeCostCenterState = this.myJoinCostTypeCostCenterFacade.getStateSnapshot();
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            data: {
                message: 'app.cost-type-distribution.confirm-delete.label',
                messageParams: {
                    costTypeName: state.selectedCostType.name,
                    costCenterName: state.costCenterLookupMap[ distribution.costCenterId ].name,
                },
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myJoinCostTypeCostCenterFacade.deleteCostTypeDistribution(distribution);
            }
        });
    }


    public clearFilter(): void {
        this.searchFormControl.setValue('');
    }

    private listenToDistributionReferenceSelectionChange(): void {
        this.distributionReferenceFormControl.valueChanges.pipe(filter(d => d !== null))
            .subscribe(async (referenceId: number) => {
                const state: IJoinCostTypeCostCenterState = this.myJoinCostTypeCostCenterFacade.getStateSnapshot();
                if (state.selectedCostType !== null && state.selectedCostType.distributionReferenceId !== referenceId) {
                    await this.myJoinCostTypeCostCenterFacade.setCostTypeDistributionReference(referenceId);
                }
            });
    }
}
